/**
 * Hyperliquid user-signed actions (spot→perps, approvals, etc.) always use
 * EIP-712 chainId 0x66eee (421614). This is a signing convention — your funds
 * stay on Hyperliquid Mainnet. No ETH or gas is required; it is an off-chain signature.
 */
export const HL_SIGN_NETWORK = {
  chainId: 421614,
  chainIdHex: '0x66eee',
  chainName: 'Arbitrum Sepolia (Hyperliquid signing)',
  rpcUrl: 'https://sepolia-rollup.arbitrum.io/rpc',
  explorerUrl: 'https://sepolia.arbiscan.io',
};

/**
 * L1 perp orders use EIP-712 chainId 1337 — a signing convention, NOT a live EVM chain.
 * There is no valid JSON-RPC URL for MetaMask; adding this network usually fails or breaks.
 * MetaMask direct L1 signing is unsupported — use agent signing (oBot pattern) or Rabby.
 */
export const HL_L1_SIGN_NETWORK = {
  chainId: 1337,
  chainIdHex: '0x539',
  chainName: 'Hyperliquid (order signing)',
  /** HL REST API — not JSON-RPC; MetaMask add-network may still fail. Prefer agent/Rabby for trades. */
  rpcUrl: 'https://api.hyperliquid.xyz',
  explorerUrl: 'https://app.hyperliquid.xyz',
};

export function isChainMismatchError(err) {
  const raw = String(err?.message || err?.data?.message || err || '').toLowerCase();
  return (
    raw.includes('chain id on message does not match') ||
    raw.includes('chainid mismatch') ||
    raw.includes('must match the active chainid') ||
    raw.includes('provided chainid') ||
    raw.includes('wrong chain') ||
    raw.includes('invalid chain')
  );
}

function normalizeChainId(id) {
  if (id == null || id === '') return null;
  if (typeof id === 'number' && Number.isFinite(id)) return id;
  const s = String(id).toLowerCase();
  if (s.startsWith('0x')) {
    const n = parseInt(s, 16);
    return Number.isFinite(n) ? n : null;
  }
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : null;
}

/** Switch to HL signing network when typed data domain chainId requires it (e.g. after Arbitrum deposit). */
async function ensureNetworkForHlTypedData(provider, typedData) {
  const required = normalizeChainId(typedData?.domain?.chainId);
  if (required !== HL_SIGN_NETWORK.chainId) return;

  const currentHex = await provider.request({ method: 'eth_chainId' }).catch(() => null);
  const current = normalizeChainId(currentHex);
  if (current === required) return;

  await tryHlSignNetworkSwitch(provider);
}

function isUserRejection(err) {
  const raw = String(err?.message || err || '').toLowerCase();
  return err?.code === 4001 || /user rejected|denied|cancelled|canceled/i.test(raw);
}

/** HL sends signatureChainId as 0x66eee — SDK parseInt() without radix breaks MetaMask signing. */
export function parseHlSignatureChainId(signatureChainId) {
  const s = String(signatureChainId ?? '').trim();
  if (!s) throw new Error('Missing signatureChainId on Hyperliquid action');
  if (s.toLowerCase().startsWith('0x')) {
    const n = parseInt(s, 16);
    if (!Number.isFinite(n)) throw new Error(`Invalid signatureChainId: ${s}`);
    return n;
  }
  const n = parseInt(s, 10);
  if (!Number.isFinite(n)) throw new Error(`Invalid signatureChainId: ${s}`);
  return n;
}

const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000';

/** Build EIP-712 payload for HL user-signed actions (approveAgent, usdClassTransfer, etc.). */
export function buildHlUserSignTypedData(action, types) {
  const primaryType = Object.keys(types)[0];
  let act = { ...action };
  if (act.type === 'approveAgent' && !act.agentName) {
    act = { ...act, agentName: '' };
  }
  const knownKeys = new Set(types[primaryType].map((f) => f.name));
  const message = Object.fromEntries(Object.entries(act).filter(([k]) => knownKeys.has(k)));

  return {
    domain: {
      name: 'HyperliquidSignTransaction',
      version: '1',
      chainId: parseHlSignatureChainId(action.signatureChainId),
      verifyingContract: ZERO_ADDRESS,
    },
    types: {
      EIP712Domain: [
        { name: 'name', type: 'string' },
        { name: 'version', type: 'string' },
        { name: 'chainId', type: 'uint256' },
        { name: 'verifyingContract', type: 'address' },
      ],
      ...types,
    },
    primaryType,
    message,
  };
}

export function splitHlSignatureHex(signature) {
  const sig = String(signature || '');
  if (sig.length !== 132) {
    throw new Error(`Invalid signature length (${sig.length}), expected 132 hex chars`);
  }
  const r = `0x${sig.slice(2, 66)}`;
  const s = `0x${sig.slice(66, 130)}`;
  let v = parseInt(sig.slice(130, 132), 16);
  if (v === 0 || v === 1) v += 27;
  if (v !== 27 && v !== 28) {
    throw new Error(`Invalid signature recovery value: ${v}`);
  }
  return { r, s, v };
}

/**
 * Sign a HL user-signed action in the browser wallet (correct chainId + network switch).
 */
export async function signHlUserSignedAction({ provider, fromAddress, action, types }) {
  const typedData = buildHlUserSignTypedData(action, types);
  const sigHex = await signHlUserTypedData(provider, fromAddress, typedData);
  return splitHlSignatureHex(sigHex);
}

function isAddChainUnsupported(err) {
  return /does not support wallet_addEthereumChain/i.test(String(err?.message || err || ''));
}

export async function tryHlL1SignNetworkSwitch(provider) {
  return tryHlSignNetworkSwitch(provider, HL_L1_SIGN_NETWORK);
}

/**
 * Best-effort switch to an Ethereum network (HL user-sign or L1 order signing).
 * Never throws for unsupported add-chain (e.g. Uniswap Wallet).
 * @returns {boolean} true if switch/add succeeded
 */
export async function tryHlSignNetworkSwitch(provider, network = HL_SIGN_NETWORK) {
  if (!provider?.request) return false;

  const chainIdHex = network.chainIdHex;
  try {
    const current = await provider.request({ method: 'eth_chainId' }).catch(() => null);
    if (current && String(current).toLowerCase() === chainIdHex.toLowerCase()) {
      return true;
    }
  } catch {
    /* continue */
  }

  try {
    await provider.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId: chainIdHex }],
    });
    return true;
  } catch (e) {
    if (isUserRejection(e)) throw e;
    if (e?.code === 4902) {
      try {
        await provider.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: chainIdHex,
              chainName: network.chainName,
              nativeCurrency: { name: 'ETH', symbol: 'ETH', decimals: 18 },
              rpcUrls: [network.rpcUrl],
              blockExplorerUrls: [network.explorerUrl],
            },
          ],
        });
        return true;
      } catch (addErr) {
        if (isUserRejection(addErr)) throw addErr;
        if (isAddChainUnsupported(addErr)) return false;
        return false;
      }
    }
    return false;
  }
}

async function rawSignTypedData(provider, fromAddress, typedData) {
  if (!provider?.request) throw new Error('Wallet does not support signing');
  return provider.request({
    method: 'eth_signTypedData_v4',
    params: [fromAddress, JSON.stringify(typedData)],
  });
}

/**
 * Sign HL user-signed EIP-712 data. Tries sign first (works on many wallets without
 * a network switch). On chain mismatch, attempts switch then retries.
 */
export async function signHlUserTypedData(provider, fromAddress, typedData) {
  await ensureNetworkForHlTypedData(provider, typedData);

  try {
    return await rawSignTypedData(provider, fromAddress, typedData);
  } catch (first) {
    if (isUserRejection(first)) throw first;
    if (!isChainMismatchError(first)) throw first;

    await tryHlSignNetworkSwitch(provider);

    try {
      return await rawSignTypedData(provider, fromAddress, typedData);
    } catch (second) {
      if (isUserRejection(second)) throw second;
      throw second;
    }
  }
}

export function formatHlSignNetworkError(err) {
  const cause = String(err?.cause?.message || err?.cause?.shortMessage || '').trim();
  const raw = err?.message || String(err);
  const combined = `${raw} ${cause}`.trim();

  if (/must deposit before performing/i.test(combined)) {
    return (
      'Hyperliquid requires at least one USDC deposit on this wallet before agent approval. ' +
      'Use Fund HL → USDC (Arbitrum) tab, deposit ~$5, wait ~1 minute, then retry Approve agent.'
    );
  }
  if (isAddChainUnsupported(err)) {
    return (
      'This wallet cannot add the Hyperliquid signing network (Uniswap Wallet limitation). ' +
      'Try Move again — we will request a signature without switching networks. ' +
      'If it still fails, connect the same 0x address in MetaMask, Rabby, or Phantom (Ethereum tab). No ETH needed.'
    );
  }
  if (isChainMismatchError(err)) {
    return (
      'MetaMask is on Arbitrum One but this Hyperliquid signature uses the HL signing network ' +
      '(Arbitrum Sepolia, chain 421614). Approve the network switch when prompted, then sign again. ' +
      'Your USDC stays on Hyperliquid Mainnet — no gas or ETH required.'
    );
  }
  if (isUserRejection(err)) {
    return 'Signature cancelled in wallet.';
  }
  if (/failed to sign typed data/i.test(combined)) {
    return (
      cause ||
      'Could not sign in your wallet. Approve the network switch to Arbitrum Sepolia (421614) if prompted, then sign again.'
    );
  }
  return cause || raw;
}

/** @deprecated Use signHlUserTypedData — kept for imports that only need switch */
export async function ensureHlSignNetwork(provider, network = HL_SIGN_NETWORK) {
  const ok = await tryHlSignNetworkSwitch(provider, network);
  if (!ok) {
    // Do not throw — signing may still work without an explicit network switch.
    return;
  }
}
